import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import Head from "next/head";
import { useRouter } from "next/router";
import { ENDPOINTS, WEB_URL } from "@/config/endpoints";
import IMAGES from "@/public/assets/img";
import { SEODATA } from "@/config/seo_config";
import useIsMobile from "@/shared/useIsMobile";
import { glossaryBreadcrumbSchema } from "@/lib/glossaryBreadcrumSchema";

export const metadata = {
  title: SEODATA.FinanceGlossary.title,
  description: SEODATA.FinanceGlossary.description,
  alternates: {
    canonical: SEODATA.FinanceGlossary.url,
  },
  openGraph: {
    title: SEODATA.FinanceGlossary.title,
    description: SEODATA.FinanceGlossary.description,
    url: SEODATA.FinanceGlossary.url,
  },
};

const FinanceGlossary = ({ glossary }) => {
  const router = useRouter();
  const isMobile = useIsMobile();
  const rightRef = useRef(null);
  const leftRef = useRef(null);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (searchTerm.trim().length >= 2) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch search results when debouncedSearchTerm updates (at least 2 characters)
  useEffect(() => {
    const trimmed = debouncedSearchTerm.trim();
    if (trimmed.length < 2) {
      setSearchResults(null);
      setIsLoading(false);
      return;
    }
    let isCancelled = false;
    const fetchSearchResults = async () => {
      try {
        const response = await fetch(
          ENDPOINTS.SEARCH_GLOSSARY_API(encodeURIComponent(trimmed))
        );
        if (!isCancelled) {
          if (response.ok) {
            const data = await response.json();
            setSearchResults(data?.data?.content || []);
          } else {
            setSearchResults([]);
          }
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Failed to search glossary:", error);
          setSearchResults([]);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };
    fetchSearchResults();
    return () => {
      isCancelled = true;
    };
  }, [debouncedSearchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setSelectedLetter(null);
  };

  const allLetters = Array.from({ length: 26 }, (_, i) =>
    String.fromCharCode(65 + i)
  );

  const initialTerms = glossary?.data?.content || [];
  const searchTerms = searchResults !== null ? searchResults : initialTerms;

  const leftOrganizedArray = allLetters.map((letter) => ({
    letter,
    terms: initialTerms.filter(
      (t) =>
        t.categories?.[0]?.name?.toUpperCase() === letter ||
        (!t.categories?.length && t.title?.charAt(0).toUpperCase() === letter)
    ),
  }));

  const rightOrganizedArray = allLetters.map((letter) => ({
    letter,
    terms: searchTerms.filter(
      (t) =>
        t.categories?.[0]?.name?.toUpperCase() === letter ||
        (!t.categories?.length && t.title?.charAt(0).toUpperCase() === letter)
    ),
  }));

  const handleLetterClick = (letter) => {
    if (selectedLetter === letter) {
      setSelectedLetter(null);
    } else {
      setSelectedLetter(letter);
    }
    const rightContainer = rightRef.current;
    const leftContainer = leftRef.current;
    const rightElement = document.getElementById(`letter-${letter}`);
    const leftElement = document.getElementById(`left-letter-${letter}`);
    // 👉 Scroll RIGHT
    if (rightContainer && rightElement) {
      rightContainer.scrollTo({
        top: rightElement.offsetTop - 20,
        behavior: "smooth",
      });
    }
    // 👉 Scroll LEFT
    if (leftContainer && leftElement) {
      leftContainer.scrollTo({
        top: leftElement.offsetTop - 125,
        behavior: "smooth",
      });
    }
  };

  const handleTermClick = (slug) => {
    router.push(`/finance-glossary/${slug}`);
  };

  const displayedRightArray = selectedLetter
    ? rightOrganizedArray.filter((l) => l.letter === selectedLetter)
    : rightOrganizedArray;

  const hasNoRightResults = displayedRightArray.every(
    (item) => item.terms.length === 0
  );

  const getFirstParagraph = (html) => {
    const match = html.match(/<p>(.*?)<\/p>/);
    return match ? match[1] : "";
  };

  const breadcrumbSchema = glossaryBreadcrumbSchema({
    homeUrl: WEB_URL,
  });

  const renderSearchBar = () => (
    <div className="relative flex items-center bg-white border border-bordercolor rounded-lg px-1 py-1 transition-all mb-2">
      <svg
        className="w-2 h-2 text-brand mr-2.5 flex-shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="https://www.w3.org/2000/svg"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
      <input
        type="text"
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder="Search Here..."
        className="w-full text-sm text-baseBlack outline-none border-none p-0 focus:outline-none focus:ring-0"
      />
      {isLoading && (
        <div className="animate-spin rounded-full h-2 w-2 border-2 border-brand border-t-transparent ml-1 flex-shrink-0"></div>
      )}
    </div>
  );

  return (
    <main className="pt-[115px]">
      <Head>
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
        <meta property="og:title" content={metadata.openGraph.title} />
        <meta
          property="og:description"
          content={metadata.openGraph.description}
        />
        <meta property="og:url" content={metadata.openGraph.url} />
        <link rel="canonical" href={metadata.alternates.canonical} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbSchema)
          }}
        />
      </Head>

      <section>
        <div className="relative bg-no-repeat bg-cover py-6">
          <Image
            priority
            src={IMAGES.ImportFactoring.ImportBGBanner.src}
            width={1366}
            height={550}
            className="absolute inset-0 w-full h-full -z-[1]"
            alt="Banner"
          />
          <div className="container px-3">
            <h1 className="text-center text-heading5 md:text-heading3 sm:text-xl font-medium text-white">
              Trade Finance Glossary and Export Import Terms
            </h1>
          </div>
        </div>

        <div className="container px-3 mt-6">
          {/* Breadcrumb */}
          <p className="text-sm text-secondary mb-3">
            <Link href={WEB_URL}>
              <span className="text-[#2369d8] cursor-pointer">Home</span>
            </Link>
            {" > "}
            <span className="font-medium">Finance Glossary</span>
          </p>

          {!isMobile ? (
            <div className="flex gap-2 mb-2">
              {/* LEFT */}
              <div className="w-1/3 sticky top-20 h-[calc(100vh-240px)] flex flex-col">
                {renderSearchBar()}
                {/* Alphabet */}
                <ul className="flex flex-wrap pb-3">
                  {leftOrganizedArray.map((item, index) => {
                    const isDisabled = item.terms.length === 0;
                    const isSelected = selectedLetter === item.letter;
                    return (
                      <li
                        key={index}
                        onClick={() =>
                          !isDisabled && handleLetterClick(item.letter)
                        }
                        className={`w-4 h-4 flex items-center justify-center text-xs border rounded mr-xsp mt-xsp ${isDisabled
                          ? "opacity-50 cursor-not-allowed"
                          : isSelected
                            ? "text-brand border-brand font-semibold cursor-pointer"
                            : "cursor-pointer hover:bg-gray-100"
                          }`}
                      >
                        {item.letter}
                      </li>
                    );
                  })}
                </ul>

                {/* Terms List */}
                <div
                  className="bg-[#F5F9FC] rounded-lg p-3 flex-1 overflow-y-auto min-h-0"
                  ref={leftRef}
                >
                  {leftOrganizedArray.map((item) =>
                    item.terms.length > 0 && (
                      <div
                        key={item.letter}
                        id={`left-letter-${item.letter}`}
                        className="pb-3"
                      >
                        <p className="text-base font-medium text-black">
                          {item.letter}
                        </p>
                        {item.terms.map((term, i) => (
                          <p
                            key={i}
                            onClick={() => handleTermClick(term.slug)}
                            className="text-sm mt-xsp cursor-pointer hover:text-primary"
                          >
                            {term.title}
                          </p>
                        ))}
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* RIGHT */}
              <div
                className="w-2/3 overflow-y-auto max-h-[calc(100vh-240px)]"
                ref={rightRef}
              >
                {hasNoRightResults && !isLoading ? (
                  <p className="bg-white  p-4 text-base text-baseBlack">
                    No terms found. Try adjusting your search or filter.
                  </p>
                ) : (
                  displayedRightArray.map((item) =>
                    item.terms.length > 0 ? (
                      <div
                        key={item.letter}
                        id={`letter-${item.letter}`}
                        className="p-3 mb-4 bg-[#F5F9FC] rounded-lg"
                      >
                        <p className="text-lg font-medium text-brand">
                          {item.letter}
                        </p>
                        {item.terms.map((term, i) => (
                          <div key={i} className="py-1 px-xsp rounded">
                            <Link
                              href={`${WEB_URL}/finance-glossary/${term.slug}`}
                              className="text-base font-medium text-black mt-1.5 hover:text-brand"
                            >
                              {term.title}
                            </Link>
                            {term.content && (
                              <p
                                className="text-sm text-baseBlack mt-xsp"
                                dangerouslySetInnerHTML={{
                                  __html: getFirstParagraph(term.content),
                                }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    ) : null
                  )
                )}
              </div>
            </div>
          ) : (
            // Mobile Version
            <div className=" relative max-w-screen-2xl mx-auto py-2">
              {renderSearchBar()}
              <div className="w-full leftSection mb-3">
                <ul className="flex flex-wrap gap-xsp">
                  {leftOrganizedArray.map((item, index) => {
                    const isDisabled = item.terms.length === 0;
                    const isSelected = selectedLetter === item.letter;
                    return (
                      <li
                        key={index}
                        onClick={() =>
                          !isDisabled && handleLetterClick(item.letter)
                        }
                        className={`w-4 h-4 flex items-center justify-center text-xs border rounded ${isDisabled
                          ? "opacity-40"
                          : isSelected
                            ? "text-brand border-brand font-semibold cursor-pointer"
                            : "cursor-pointer hover:bg-gray-100 active:bg-gray-200"
                          }`}
                      >
                        {item.letter}
                      </li>
                    );
                  })}
                </ul>
              </div>
              {/* Terms List */}
              <div className="w-full rightSection mt-1 overflow-y-auto max-h-[calc(100vh-300px)]">
                {hasNoRightResults && !isLoading ? (
                  <p className="bg-white py-4 text-sm text-baseBlack">
                    No terms found. Try adjusting your search or filter.
                  </p>
                ) : (
                  displayedRightArray.map((item) =>
                    item.terms.length > 0 ? (
                      <div
                        key={item.letter}
                        id={`letter-${item.letter}`}
                        className=" bg-[#F5F9FC] p-2 rounded-lg mt-2 sm:mb-2"
                      >
                        <p className="text-base font-semibold pt-1">
                          {item.letter}
                        </p>
                        {item.terms.map((term, i) => (
                          <div key={i} className="py-1 px-xsp rounded">
                            <Link
                              href={`${WEB_URL}/finance-glossary/${term.slug}`}
                              className="text-base font-medium text-black mt-1.5 hover:text-brand"
                            >
                              {term.title}
                            </Link>
                            {term.content && (
                              <p
                                className="text-sm text-baseBlack mt-xsp"
                                dangerouslySetInnerHTML={{
                                  __html: getFirstParagraph(term.content),
                                }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    ) : null
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default FinanceGlossary;

export async function getServerSideProps(context) {
  const { res } = context;

  try {
    const response = await fetch(`${ENDPOINTS.GLOSSARY_API}`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const glossaryData = await response.json();

    return {
      props: {
        glossary: glossaryData,
      },
    };
  } catch (error) {
    console.error("Failed:", error.message);
    res.statusCode = 500;
    return { notFound: true };
  }
}